import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';

import { ConfirmModal } from '@/components/ConfirmModal';
import { useCurrentUser } from '@/hooks/useUserRole';
import { formatAbsoluteTime } from '@/utils/date';

import { isInBriefingScope } from '../components/briefing/api';
import { useBriefingRows } from '../components/briefing/useBriefingRows';
import { describeBriefError } from '../components/basket/api';
import { LineGroup } from '../components/basket/LineGroup';
import { useResolvedAssets } from '../components/basket/useBasketAssets';
import {
  openDraftInBasket,
  requestOpenOnArrival,
  storedBasketCount,
  storedLinkedDraft,
} from '../components/basket/useBasket';
import { formatDateDDMMYYYY, getAssetExpiryDate, isAssetExpired } from '../constants/taxonomy';
import { useAssetLibraryPermissions } from '../useAssetLibraryPermissions';
import {
  channelLabel,
  deleteBrief,
  getBrief,
  ROW_REJECTED_REASON,
  rowOutcomes,
  submitBrief,
  summarizeBrief,
  updateBrief,
  type BriefLineOut,
  type BriefOut,
  type RowOutcome,
} from './api';
import { useDraftEdits } from './useDraftEdits';

/**
 * One brief, opened from My Briefs: its lines grouped by media-plan row, the
 * way the basket groups them (`LineGroup`, shared with it).
 *
 * A requested brief is a record: what was ordered and what was skipped, and
 * why. A draft is unfinished work that may have gone stale while it sat, so on
 * open it says what has changed under it — a row no longer waiting for a
 * brief, an asset whose rights have run out, a submit that went nowhere —
 * before anyone starts on it, and can then be edited, sent or deleted here.
 */
export function BriefDrawer({ briefId, onClose }: { briefId: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['briefs', 'detail', briefId],
    queryFn: () => getBrief(briefId),
    // A 404 is an answer: not theirs, or gone. Retrying can't change it.
    // Anything else gets one quiet retry, then "Try again" is the user's.
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 1,
  });

  // Unsaved edits to a draft are asked about before the drawer goes, however
  // it is closed: the button, the backdrop or Escape.
  const [isDirty, setIsDirty] = useState(false);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const requestClose = () => (isDirty ? setIsConfirmingClose(true) : onClose());

  // A prompt open on top (ConfirmModal) takes Escape for itself before this
  // sees it, so here Escape only ever means the drawer.
  const dialogRef = useModalBehaviour(requestClose);

  // Into `document.body`, like the asset overlay: inside the page's `main`,
  // which the suite sidebar is painted over, no z-index would cover the
  // sidebar, and it stayed clickable above the backdrop.
  return createPortal(
    <div className="fixed inset-0 z-[80] flex justify-end">
      <button
        type="button"
        aria-label="Close brief"
        // The close button inside is the keyboard's way out; this is the mouse's.
        tabIndex={-1}
        onClick={requestClose}
        className="flex-1 bg-slate-900/30 backdrop-blur-[2px]"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Brief"
        tabIndex={-1}
        className="flex h-full w-full max-w-[560px] flex-col border-l border-slate-200 bg-white shadow-2xl focus:outline-none"
      >
        {query.data ? (
          // Whenever there is a brief to show, even after a refresh failed:
          // TanStack keeps the data on a failed refetch, and swapping the
          // editor for an error would throw away the user's unsaved edits.
          <BriefDetail
            brief={query.data}
            onClose={requestClose}
            onDeleted={onClose}
            onDirtyChange={setIsDirty}
            refreshFailed={query.isError}
            onRetry={() => void query.refetch()}
          />
        ) : query.isPending ? (
          <Centered onClose={onClose}>
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-label="Loading brief" />
          </Centered>
        ) : query.isError ? (
          isNotFound(query.error) ? (
            // Same words for "not yours" and "doesn't exist": the backend
            // doesn't say which, and neither should we.
            <Centered onClose={onClose}>
              <h2 className="text-[15.5px] font-bold text-slate-800">
                This brief isn’t available to you
              </h2>
              <p className="mt-2 text-[12.5px] text-slate-500">
                It may have been deleted, or it belongs to someone else.
              </p>
            </Centered>
          ) : (
            <Centered onClose={onClose}>
              <h2 className="text-[15.5px] font-bold text-slate-800">Couldn’t load this brief</h2>
              <p className="mt-2 text-[12.5px] text-slate-500">
                This is a loading problem, not a change to the brief.
              </p>
              <button
                type="button"
                onClick={() => void query.refetch()}
                className="mt-3 rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-[#0010a3]"
              >
                Try again
              </button>
            </Centered>
          )
        ) : null}
        <ConfirmModal
          isOpen={isConfirmingClose}
          onClose={() => setIsConfirmingClose(false)}
          onConfirm={onClose}
          title="Discard your changes?"
          message="Your edits to this draft haven’t been saved."
          confirmText="Discard"
          cancelText="Keep editing"
          variant="warning"
        />
      </div>
    </div>,
    document.body,
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * What any modal owes the keyboard: focus moves in on open and comes back to
 * whatever opened it on close, Tab stays inside, Escape calls `onEscape`, and
 * the page behind doesn't scroll. Without it, Tab walked the hidden list behind the
 * backdrop and a screen reader was never told the drawer had opened.
 */
function useModalBehaviour(onEscape: () => void) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape();
        return;
      }
      const dialog = dialogRef.current;
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !dialog.contains(active);
      if (event.shiftKey && (active === first || active === dialog || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onEscape]);

  return dialogRef;
}

function BriefDetail({
  brief,
  onClose,
  onDeleted,
  onDirtyChange,
  refreshFailed,
  onRetry,
}: {
  brief: BriefOut;
  /** Asks first if there are unsaved edits. */
  onClose: () => void;
  /** Closes outright: after a delete there is nothing left to keep. */
  onDeleted: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  /** A background refresh failed; what is shown is the last brief loaded. */
  refreshFailed: boolean;
  onRetry: () => void;
}) {
  const isDraft = brief.status === 'draft';
  const summary = summarizeBrief(brief);
  const outcomes = useMemo(() => rowOutcomes(brief.lines), [brief.lines]);

  // Who may do what. Editing and deleting: the creator, or an admin cleaning
  // up. Sending: only the creator — the backend has no admin bypass for it.
  const { permissions } = useAssetLibraryPermissions();
  const { data: me } = useCurrentUser();
  const isOwner = !!me?.email && me.email.toLowerCase() === brief.created_by.toLowerCase();
  const canEdit = isDraft && (isOwner || permissions.can_manage_all_briefs);
  const canSend = isDraft && isOwner;

  const edits = useDraftEdits(brief);
  useEffect(() => onDirtyChange(canEdit && edits.isDirty), [canEdit, edits.isDirty, onDirtyChange]);
  // The edits go with the editor: once it's gone there is nothing left to ask
  // about discarding.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const shownLines = canEdit ? edits.lines : brief.lines;

  // In line order, which is the order they were picked in.
  const groups = useMemo(() => {
    const byRow = new Map<string, BriefLineOut[]>();
    for (const line of shownLines) {
      byRow.set(line.campaign_id, [...(byRow.get(line.campaign_id) ?? []), line]);
    }
    return Array.from(byRow, ([rowId, lines]) => ({ rowId, lines }));
  }, [shownLines]);

  const assetIds = useMemo(() => brief.lines.map((line) => line.asset_id), [brief.lines]);
  const { assetById, urls } = useResolvedAssets(assetIds);

  // Which of a draft's rows have moved on since it was saved. Only once the
  // rows have actually loaded: a failed or pending lookup is not evidence that
  // every row is gone. Not fetched at all for a requested brief, which was
  // judged when it was sent; always fetched afresh for a draft, since the
  // library's copy can be minutes old (and checked again on Send, below).
  const {
    rows: waitingRows,
    isLoading: rowsLoading,
    isError: rowsFailed,
    refetch: refetchWaitingRows,
  } = useBriefingRows(undefined, { enabled: isDraft, refetchOnMount: 'always' });
  const waitingRowIds = useMemo(() => new Set(waitingRows.map((row) => row.id)), [waitingRows]);
  const canJudgeRows = isDraft && !rowsLoading && !rowsFailed;

  const lastSubmitFailed = isDraft && summary.skippedRowCount > 0;

  // Whether a row has left the waiting list, judged against `waiting`. Only
  // rows the lookup covers: one outside its platform and format (or saved
  // before its format was kept) would always read as gone.
  const hasMovedOn = (lines: BriefLineOut[], rowId: string, waiting: ReadonlySet<string>) =>
    isInBriefingScope(lines[0]) && !waiting.has(rowId);

  // On screen: a skipped row already shows why, so it gets no second notice.
  const isStale = (rowId: string, lines: BriefLineOut[]) =>
    canJudgeRows &&
    outcomes.get(rowId)?.status !== 'skipped' &&
    hasMovedOn(lines, rowId, waitingRowIds);

  // ----- actions
  const queryClient = useQueryClient();
  const filenameFor = (assetId: string) => assetById.get(assetId)?.filename_original;

  // A fresh copy of the brief replaces the cached one, so the drawer shows
  // what the server now holds; the lists refetch for their counts and tabs.
  const showSaved = (saved: BriefOut) => {
    queryClient.setQueryData(['briefs', 'detail', saved.id], saved);
    void queryClient.invalidateQueries({ queryKey: ['briefs', 'list'] });
    edits.reset();
  };

  const save = useMutation({
    mutationFn: () => updateBrief(brief.id, edits.buildPatch()),
    onSuccess: (saved) => {
      showSaved(saved);
      toast.success('Draft saved');
    },
    onError: (error) =>
      toast.error(describeBriefError(error, filenameFor, 'Could not save', 'the draft')),
  });

  /**
   * Unsaved edits go first, in one save with any stale rows the user agreed to
   * drop, then the submit. A failed submit after a good save leaves a saved
   * draft, which is what the user asked for anyway.
   */
  const send = useMutation({
    mutationFn: async (dropLines: BriefLineOut[]) => {
      // Skipped lines are never judged again on their own: submit only checks
      // pending ones. Re-saving the line set makes them all pending, so a
      // draft whose cause has since been fixed (rights renewed, say) can go;
      // an asset still invalid is refused by name instead of being skipped
      // again with the old reason.
      const resendLines = edits.lines.some((line) => line.status === 'skipped');
      const patch = edits.buildPatch(dropLines, { resendLines });
      if (Object.keys(patch).length > 0) showSaved(await updateBrief(brief.id, patch));
      return submitBrief(brief.id);
    },
    onSuccess: (result) => {
      showSaved(result.brief);
      if (result.promoted_count === 0) {
        toast.error('Nothing could be sent. Each row says why.');
      } else {
        toast.success(
          `Sent ${result.brief.reference ?? ''}${
            result.skipped_count > 0 ? `, with ${result.skipped_count} skipped` : ''
          }`,
        );
      }
    },
    onError: (error) =>
      toast.error(describeBriefError(error, filenameFor, 'Could not send', 'the draft')),
  });

  const remove = useMutation({
    mutationFn: () => deleteBrief(brief.id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['briefs', 'detail', brief.id] });
      void queryClient.invalidateQueries({ queryKey: ['briefs', 'list'] });
      toast.success('Draft deleted');
      onDeleted();
    },
    onError: () => toast.error('Could not delete the draft'),
  });

  const [confirming, setConfirming] = useState<
    'delete' | 'stale' | 'all-stale' | 'replace' | 'changed' | null
  >(null);

  // ----- open in basket: to add assets, which only the library can do
  const navigate = useNavigate();
  // A draft from before briefs covered one market has none to link by.
  const canReopen = canSend && brief.market !== null;
  const [replacing, setReplacing] = useState({ count: 0, fromOtherDraft: false });

  const goToBasket = ({ resume }: { resume: boolean }) => {
    // Already open in the basket and unchanged since: pick it up where it
    // was, unsaved additions and all. Otherwise load the saved version.
    if (resume) {
      requestOpenOnArrival();
    } else {
      openDraftInBasket(
        {
          id: brief.id,
          market: brief.market!,
          createdAt: brief.created_at,
          updatedAt: brief.updated_at,
        },
        brief.lines.map((line) => ({ rowId: line.campaign_id, assetId: line.asset_id })),
        { neededBy: brief.needed_by ?? '', notes: brief.notes ?? '' },
      );
    }
    void navigate({ to: '/assets' });
  };

  // The basket holds one thing at a time: other picks are replaced, after
  // asking, never merged into the draft unseen.
  //
  // The basket's save replaces the draft's whole line set, so resuming a
  // basket opened before the draft was changed here would undo that change.
  // Resume only the version the basket last saw; for a newer one, ask.
  const handleOpenInBasket = () => {
    const count = storedBasketCount();
    const linkedNow = storedLinkedDraft();
    if (count > 0 && linkedNow?.id === brief.id) {
      if (linkedNow.updatedAt === brief.updated_at) goToBasket({ resume: true });
      else setConfirming('changed');
    } else if (count > 0) {
      setReplacing({ count, fromOtherDraft: linkedNow !== null });
      setConfirming('replace');
    } else {
      goToBasket({ resume: false });
    }
  };
  // The rows the check at Send found moved on: what the prompt counts and what
  // "Remove and send" drops, exactly as found, not as the list next renders.
  const [staleRows, setStaleRows] = useState<{ rowId: string; lines: BriefLineOut[] }[]>([]);
  const [isCheckingRows, setIsCheckingRows] = useState(false);
  const busy = save.isPending || send.isPending || remove.isPending || isCheckingRows;

  // The rows are asked about again at the moment of sending: the drawer may
  // have been open a while, and this is where a row that moved on would
  // otherwise be ordered. A failed check is no evidence either way, so the
  // send then goes ahead on what was known, as it would have before.
  //
  // Judged only from what that fetch returns, not from whether the drawer's
  // own lookup had loaded: one still loading at the click would otherwise
  // wave everything through. And every row, skipped ones too — a save that
  // changes the lines makes all of them pending again, so a row skipped last
  // time can be ordered this time.
  const handleSend = async () => {
    setIsCheckingRows(true);
    const fresh = await refetchWaitingRows();
    setIsCheckingRows(false);
    if (!fresh.data) {
      send.mutate([]);
      return;
    }
    const waiting = new Set(fresh.data.map((row) => row.id));
    const stale = groups.filter(({ rowId, lines }) => hasMovedOn(lines, rowId, waiting));
    if (stale.length > 0 && stale.length === groups.length) {
      // Removing them all would leave an empty draft, which can't be sent:
      // the only way forward is deleting it.
      setConfirming('all-stale');
    } else if (stale.length > 0) {
      setStaleRows(stale);
      setConfirming('stale');
    } else {
      send.mutate([]);
    }
  };

  // A draft keeps at least one asset: an empty one can't be sent, and nothing
  // can be added back to it. Clearing it out is what Delete is for.
  const isEmptied = groups.length === 0;

  return (
    <>
      <header className="flex items-start gap-3 border-b border-slate-200 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-[16px] font-bold text-slate-800">
              {brief.reference ?? 'Draft brief'}
            </h2>
            <Tag tone={isDraft ? 'slate' : 'blue'}>{isDraft ? 'Draft' : 'Requested'}</Tag>
          </div>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
            <Term label="Channel">{channelLabel(brief.channel)}</Term>
            <Term label="Markets">{summary.markets.join(', ') || '—'}</Term>
            <Term label="Created">{formatAbsoluteTime(brief.created_at)}</Term>
            {brief.requested_at && (
              <Term label="Sent">{formatAbsoluteTime(brief.requested_at)}</Term>
            )}
            <Term label="Needed by">
              {canEdit ? (
                <input
                  type="date"
                  aria-label="Needed by"
                  disabled={busy}
                  value={edits.neededBy}
                  onChange={(event) => edits.setNeededBy(event.target.value)}
                  className="rounded border border-slate-200 px-1.5 py-0.5 text-[12px] text-slate-700 focus:border-[#0014cc] focus:outline-none"
                />
              ) : (
                formatDateDDMMYYYY(brief.needed_by) || '—'
              )}
            </Term>
          </dl>
        </div>
        <CloseButton onClose={onClose} />
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {refreshFailed && (
          <div
            role="status"
            className="mb-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[12px] text-slate-600"
          >
            Couldn’t refresh this brief. Showing the last loaded version.
            <button
              type="button"
              onClick={onRetry}
              className="ml-auto font-medium text-[#0014cc] hover:underline"
            >
              Try again
            </button>
          </div>
        )}
        {lastSubmitFailed && (
          <Banner tone="amber" title="Not sent: none of these rows could be requested">
            Each row below says why. Fix or remove them before submitting again.
          </Banner>
        )}
        {!isDraft && (
          <Banner
            tone={summary.skippedRowCount > 0 ? 'amber' : 'green'}
            title={
              summary.skippedRowCount > 0
                ? `Partially sent: ${summary.orderedRowCount} of ${summary.rowCount} rows`
                : `All ${summary.rowCount} row${summary.rowCount === 1 ? '' : 's'} sent`
            }
          >
            {summary.skippedRowCount > 0 &&
              'The skipped rows were not sent, so nobody is working on them. Each one says why.'}
          </Banner>
        )}

        <div className="flex flex-col gap-4">
          {groups.map(({ rowId, lines }) => {
            const outcome = outcomes.get(rowId)!;
            // Only rows the lookup covers: one outside its platform and format
            // (or saved before its format was kept) would always read as gone.
            const stale = isStale(rowId, lines);
            return (
              <LineGroup
                key={rowId}
                market={lines[0].market}
                title={rowTitle(lines[0])}
                details={rowDetails(lines[0])}
                assetIds={lines.map((line) => line.asset_id)}
                assetById={assetById}
                urls={urls}
                removeLineLabel="Remove from brief"
                // Nothing changes while a save or send is out: its answer
                // replaces the draft, and an edit made meanwhile would be lost.
                removeDisabled={busy}
                onRemoveLine={
                  canEdit
                    ? (assetId) =>
                        edits.removeLines(lines.filter((line) => line.asset_id === assetId))
                    : undefined
                }
                onRemoveRow={canEdit ? () => edits.removeLines(lines) : undefined}
                status={isDraft ? undefined : <OutcomeTag outcome={outcome} />}
                notice={
                  outcome.status === 'skipped' ? (
                    <Notice>{outcome.reason ?? 'Not eligible when it was submitted.'}</Notice>
                  ) : stale ? (
                    // Neutral on purpose: submitting a saved draft checks the
                    // assets, not the row, so this row would still be ordered;
                    // and the row's absence doesn't say why it left.
                    <Notice>
                      This row is no longer waiting for a brief. Check it’s still needed before
                      submitting.
                    </Notice>
                  ) : undefined
                }
                assetNote={(assetId) => {
                  // Each skipped asset says its own reason, so the one that
                  // sank the row stands out from its neighbours'
                  // "rejected alongside".
                  const reason = lines.find((line) => line.asset_id === assetId)?.result_reason;
                  if (reason) {
                    return (
                      <p
                        className={`text-[10.5px] ${
                          reason === ROW_REJECTED_REASON
                            ? 'text-slate-400'
                            : 'font-medium text-amber-700'
                        }`}
                      >
                        {reason === ROW_REJECTED_REASON
                          ? 'Skipped with the rest of this row'
                          : reason}
                      </p>
                    );
                  }
                  // A requested brief was judged when it was sent; only a
                  // draft can have gone stale since.
                  const asset = assetById.get(assetId);
                  if (!isDraft || !asset || !isAssetExpired(asset)) return null;
                  return (
                    <p className="text-[10.5px] font-medium text-amber-700">
                      Rights expired on {formatDateDDMMYYYY(getAssetExpiryDate(asset))}
                    </p>
                  );
                }}
              />
            );
          })}
        </div>

        {isEmptied && (
          <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-[12.5px] text-slate-500">
            A draft needs at least one asset. Discard your changes to keep it, or delete the draft.
          </p>
        )}

        {canEdit ? (
          <label className="mt-5 flex flex-col gap-1.5 border-t border-slate-200 pt-4">
            <span className="text-[12px] font-semibold text-slate-700">Localisation brief</span>
            <textarea
              value={edits.notes}
              disabled={busy}
              onChange={(event) => edits.setNotes(event.target.value)}
              rows={4}
              placeholder="What the market needs applied — legal line, offer, endframe copy, language…"
              className="resize-y rounded-lg border border-slate-200 px-2.5 py-2 text-[12.5px] leading-relaxed text-slate-700 placeholder:text-slate-400 focus:border-[#0014cc] focus:outline-none"
            />
          </label>
        ) : (
          brief.notes && (
            <section className="mt-5 border-t border-slate-200 pt-4">
              <h3 className="text-[12px] font-semibold text-slate-700">Localisation brief</h3>
              <p className="mt-1.5 whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-600">
                {brief.notes}
              </p>
            </section>
          )
        )}
      </div>

      {canEdit && (
        <footer className="flex items-center gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming('delete')}
            className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:text-slate-400"
          >
            Delete draft
          </button>
          <span className="ml-auto" />
          {edits.isDirty && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => edits.reset()}
                className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-slate-500 hover:bg-slate-50 disabled:text-slate-400"
              >
                Discard changes
              </button>
              <button
                type="button"
                disabled={busy || isEmptied}
                title={isEmptied ? 'A draft needs at least one asset' : undefined}
                onClick={() => save.mutate()}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          {canReopen && (
            <button
              type="button"
              disabled={busy || edits.isDirty}
              title={
                edits.isDirty ? 'Save or discard your changes first' : 'Add assets in the library'
              }
              onClick={handleOpenInBasket}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              Open in basket
            </button>
          )}
          {canSend ? (
            <button
              type="button"
              disabled={busy || isEmptied}
              title={isEmptied ? 'Nothing left to send' : undefined}
              onClick={() => void handleSend()}
              className="rounded-lg bg-[#0014cc] px-3 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#0010a3] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              {send.isPending ? 'Sending…' : 'Send to production'}
            </button>
          ) : (
            <span className="text-[11.5px] text-slate-400">
              Only the person who made a draft can send it.
            </span>
          )}
        </footer>
      )}

      <ConfirmModal
        isOpen={confirming === 'replace'}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          goToBasket({ resume: false });
        }}
        title="Replace your basket with this draft?"
        message={`Your basket has ${replacing.count} asset${replacing.count === 1 ? '' : 's'}${
          replacing.fromOtherDraft ? ' from another draft' : ''
        }. They'll be removed from the basket.`}
        confirmText="Replace basket"
        variant="warning"
      />
      <ConfirmModal
        isOpen={confirming === 'changed'}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          goToBasket({ resume: false });
        }}
        title="This draft changed since it went into your basket"
        message="Load the latest version? Changes in your basket that weren’t saved will be replaced."
        confirmText="Load latest"
        variant="warning"
      />
      <ConfirmModal
        isOpen={confirming === 'delete'}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          remove.mutate();
        }}
        title="Delete this draft?"
        message="It can’t be brought back. The assets stay in the library."
        confirmText="Delete draft"
        variant="danger"
        isLoading={remove.isPending}
      />
      <ConfirmModal
        isOpen={confirming === 'stale'}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          send.mutate(staleRows.flatMap(({ lines }) => lines));
        }}
        title={`${staleRows.length} row${staleRows.length === 1 ? ' is' : 's are'} no longer waiting for a brief`}
        message="They’ll be removed from this draft, and the rest sent to production."
        confirmText="Remove and send"
        variant="warning"
      />
      <ConfirmModal
        isOpen={confirming === 'all-stale'}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          remove.mutate();
        }}
        title="None of these rows are waiting for a brief any more"
        message="There’s nothing left to send. Delete the draft?"
        confirmText="Delete draft"
        variant="danger"
        isLoading={remove.isPending}
      />
    </>
  );
}

/**
 * The row's name: its campaign code where the line kept one, otherwise what
 * the line does know about it. Lines saved before the code was captured have
 * none, and a row that has since gone has nothing left to look it up by.
 */
export function rowTitle(line: BriefLineOut): string {
  if (line.campaign_code) return line.campaign_code;
  return (
    [line.market, line.nameplate, line.platform, line.size].filter(Boolean).join(' · ') || 'Row'
  );
}

export function rowDetails(line: BriefLineOut): string {
  return [line.quarter, line.platform, line.format, line.size].filter(Boolean).join(' · ');
}

function OutcomeTag({ outcome }: { outcome: RowOutcome }) {
  if (outcome.status === 'ordered') return <Tag tone="green">Ordered</Tag>;
  if (outcome.status === 'skipped') return <Tag tone="amber">Skipped</Tag>;
  return <Tag tone="slate">Pending</Tag>;
}

function isNotFound(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

function Centered({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <>
      <div className="flex justify-end px-5 py-4">
        <CloseButton onClose={onClose} />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        {children}
      </div>
    </>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="ml-auto rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
    >
      <X className="h-4 w-4" strokeWidth={2} />
    </button>
  );
}

function Term({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-slate-700">{children}</dd>
    </>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 border-b border-amber-100 bg-amber-50 px-3 py-1.5 text-[11.5px] text-amber-800">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} />
      {children}
    </p>
  );
}

const BANNER_TONES = {
  amber: 'border-amber-200 bg-amber-50 text-amber-900',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-900',
} as const;

function Banner({
  tone,
  title,
  children,
}: {
  tone: keyof typeof BANNER_TONES;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div role="status" className={`mb-4 rounded-lg border px-3.5 py-3 ${BANNER_TONES[tone]}`}>
      <p className="text-[12.5px] font-semibold">{title}</p>
      {children && <p className="mt-1 text-[11.5px] leading-relaxed">{children}</p>}
    </div>
  );
}

const TAG_TONES = {
  slate: 'bg-slate-100 text-slate-600',
  blue: 'bg-[#0014cc]/10 text-[#0014cc]',
  amber: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
} as const;

function Tag({ tone, children }: { tone: keyof typeof TAG_TONES; children: ReactNode }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-medium ${TAG_TONES[tone]}`}
    >
      {children}
    </span>
  );
}
